/**
 * Hyper v1.4.0 - Select first currently available follow-up option.
 * Works with the stock CSS that hides input.disabled + label.
 * Uses real radio.click() so Hyper receives its normal variant change.
 * Does not assume a product.variants JSON list (suitable for large catalogs).
 */
(() => {
  const PICKER_SELECTOR = 'variant-selects';
  const sessions = new WeakMap();
  // Short fallback between choices; real stock-state updates are handled immediately.
  const STEP_DELAY = 80;

  function getGroups(picker) {
    return Array.from(picker.querySelectorAll('.product-form__input'))
      .filter(group => group.querySelector('input[type="radio"]'));
  }

  function usable(input) {
    if (input.disabled || input.classList.contains('disabled') || input.hidden) {
      return false;
    }
    const label = input.labels?.[0] || null;
    return !label?.hidden && (!label || getComputedStyle(label).display !== 'none');
  }

  function firstAvailable(group) {
    return Array.from(group.querySelectorAll('input[type="radio"]')).find(usable);
  }

  function invalidIndex(picker) {
    return getGroups(picker).findIndex(group => {
      const checked = group.querySelector('input[type="radio"]:checked');
      return !checked || !usable(checked);
    });
  }

  function schedule(picker, delay = STEP_DELAY) {
    const session = sessions.get(picker);
    if (!session || !picker.isConnected) return;
    clearTimeout(session.timer);
    session.timer = setTimeout(() => advance(picker), delay);
  }

  function advance(picker) {
    const session = sessions.get(picker);
    if (!session || !picker.isConnected || session.nextIndex < 0) return;
    const groups = getGroups(picker);
    if (session.nextIndex >= groups.length) {
      session.nextIndex = -1;
      return;
    }

    const index = session.nextIndex;
    const input = firstAvailable(groups[index]);
    if (!input) {
      if (++session.retries <= 6) schedule(picker, STEP_DELAY);
      else {
        console.warn('[Variant auto-first] No available option in group:', index);
        session.nextIndex = -1;
      }
      return;
    }

    session.retries = 0;
    session.nextIndex = index + 1;
    if (!input.checked) {
      // A native click updates checked state AND lets Hyper update its
      // selected variant, price, media, and Add to Cart button.
      session.internalClick = true;
      try {
        input.click();
      } finally {
        session.internalClick = false;
      }
      schedule(picker);
    } else {
      schedule(picker, 20);
    }
  }

  function init(picker) {
    if (sessions.has(picker) || getGroups(picker).length === 0) return;
    const session = {
      nextIndex: -1,
      retries: 0,
      timer: null,
      internalClick: false,
      observer: null
    };
    sessions.set(picker, session);

    picker.addEventListener('change', event => {
      if (session.internalClick) return;
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      const groups = getGroups(picker);
      const changedIndex = groups.findIndex(group => group.contains(input));
      if (changedIndex < 0) return;
      // User changed Size -> reset Color then Pack.
      // User changed Color -> reset Pack. Changing Pack -> leave it alone.
      session.nextIndex = changedIndex + 1;
      session.retries = 0;
      schedule(picker);
    }, true);

    session.observer = new MutationObserver(records => {
      // React as soon as Hyper changes the radio availability or re-renders
      // option controls. A short fallback covers updates with no DOM changes.
      const stockUpdated = records.some(record => {
        if (record.type === 'attributes') {
          return record.target.matches?.('input[type="radio"]');
        }
        return [...record.addedNodes, ...record.removedNodes].some(node =>
          node.nodeType === 1 && (
            node.matches?.('input[type="radio"], .product-form__input') ||
            node.querySelector?.('input[type="radio"]')
          )
        );
      });

      if (session.nextIndex >= 0) {
        if (stockUpdated) schedule(picker, 0);
      } else {
        const broken = invalidIndex(picker);
        if (broken >= 0) {
          session.nextIndex = broken;
          session.retries = 0;
          schedule(picker, 0);
        }
      }
    });
    session.observer.observe(picker, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'hidden'],
      childList: true,
      subtree: true
    });

    // Repair invalid preselected Pack when the page first opens.
    setTimeout(() => {
      if (!picker.isConnected || session.nextIndex >= 0) return;
      const broken = invalidIndex(picker);
      if (broken >= 0) {
        session.nextIndex = broken;
        schedule(picker, 0);
      }
    }, 0);
  }

  function initializeAll(root = document) {
    if (root.matches?.(PICKER_SELECTOR)) init(root);
    root.querySelectorAll?.(PICKER_SELECTOR).forEach(init);
  }

  const start = () => {
    initializeAll();
    const observer = new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType === 1) initializeAll(node);
        }
        for (const node of record.removedNodes) {
          if (node.nodeType !== 1) continue;
          const removed = [];
          if (node.matches?.(PICKER_SELECTOR)) removed.push(node);
          node.querySelectorAll?.(PICKER_SELECTOR).forEach(p => removed.push(p));
          for (const picker of removed) {
            if (picker.isConnected) continue;
            const session = sessions.get(picker);
            if (!session) continue;
            clearTimeout(session.timer);
            session.observer?.disconnect();
            sessions.delete(picker);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
})();
